import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";
import FlexBox from "../../../../components/layouts/flexbox";
import { ENUM_QUALIFY_QUESTION_TYPES } from "../../../../enums";
import { EditPreQualifyQuestions } from "./edit-prequalify-questions";
import { QUALIFY_QUESTION_TYPE_OPTIONS } from "./form-config";

type PreQualifyQuestion = {
  question?: string;
  type?: string;
  options?: string[];
};

type PreQualifyQuestionsProps = {
  questions?: Record<string, PreQualifyQuestion>;
};

function getTypeLabel(type?: string) {
  return (
    QUALIFY_QUESTION_TYPE_OPTIONS.find((opt) => opt.value === type)?.label ||
    type ||
    "—"
  );
}

function QuestionCard({
  index,
  questionKey,
  question,
}: {
  index: number;
  questionKey: string;
  question: PreQualifyQuestion;
}) {
  const showOptions =
    question.type === ENUM_QUALIFY_QUESTION_TYPES.MULTI_SELECT &&
    Array.isArray(question.options) &&
    question.options.length > 0;

  return (
    <FlexBox
      alignItems="flex-start"
      gap={2}
      p={2.5}
      border="1px solid"
      borderColor="#E2E8F0"
      borderRadius={2}
      bgcolor="#FFFFFF"
    >
      <FlexBox
        width={40}
        height={40}
        flexShrink={0}
        borderRadius={1.5}
        bgcolor="#EFF6FF"
        color="#1D4ED8"
        alignItems="center"
        justifyContent="center"
      >
        <Typography fontSize={13} fontWeight={700}>
          Q.{index + 1}
        </Typography>
      </FlexBox>

      <Stack flex={1} gap={1.25} minWidth={0}>
        <Typography
          fontSize={15}
          fontWeight={600}
          color="#0F172A"
          lineHeight={1.45}
        >
          {question.question || "Untitled question"}
        </Typography>

        <FlexBox alignItems="center" gap={1} flexWrap="wrap">
          <Typography fontSize={12} fontWeight={500} color="#64748B">
            Answer type
          </Typography>
          <Chip
            size="small"
            label={getTypeLabel(question.type)}
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: "#F1F5F9",
              color: "#334155",
            }}
          />
          <Typography fontSize={11} color="#94A3B8">
            {questionKey}
          </Typography>
        </FlexBox>

        {showOptions && (
          <Box>
            <Typography fontSize={12} fontWeight={500} color="#64748B" mb={0.75}>
              Options
            </Typography>
            <FlexBox gap={0.75} flexWrap="wrap">
              {question.options!.map((opt, i) => (
                <Chip
                  key={`${questionKey}-opt-${i}`}
                  size="small"
                  label={opt}
                  variant="outlined"
                  sx={{
                    height: 26,
                    fontSize: 12,
                    borderColor: "#CBD5E1",
                    color: "#334155",
                  }}
                />
              ))}
            </FlexBox>
          </Box>
        )}
      </Stack>
    </FlexBox>
  );
}

export default function PreQualifyQuestions({
  questions,
}: PreQualifyQuestionsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const entries = Object.entries(questions || {}) as [
    string,
    PreQualifyQuestion
  ][];

  if (isEditing) {
    return (
      <Box>
        <FlexBox alignItems="center" gap={1.5} mb={2.5}>
          <FlexBox
            width={36}
            height={36}
            borderRadius={1.5}
            bgcolor="#EFF6FF"
            color="#1D4ED8"
            alignItems="center"
            justifyContent="center"
          >
            <QuizOutlinedIcon fontSize="small" />
          </FlexBox>
          <Box>
            <Typography fontSize={18} fontWeight={700} color="#0F172A">
              Edit Pre-qualify Questions
            </Typography>
            <Typography fontSize={13} color="#64748B">
              Update question text, type, and options for this vendor
            </Typography>
          </Box>
        </FlexBox>
        <EditPreQualifyQuestions
          questions={questions}
          onCancel={() => setIsEditing(false)}
          onSaved={() => setIsEditing(false)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <FlexBox alignItems="center" justifyContent="space-between" mb={2.5}>
        <FlexBox alignItems="center" gap={1.5}>
          <FlexBox
            width={36}
            height={36}
            borderRadius={1.5}
            bgcolor="#EFF6FF"
            color="#1D4ED8"
            alignItems="center"
            justifyContent="center"
          >
            <QuizOutlinedIcon fontSize="small" />
          </FlexBox>
          <Box>
            <Typography fontSize={18} fontWeight={700} color="#0F172A">
              Pre-qualify Questions
            </Typography>
            <Typography fontSize={13} color="#64748B">
              {entries.length
                ? `${entries.length} question${
                    entries.length === 1 ? "" : "s"
                  } configured`
                : "No questions configured yet"}
            </Typography>
          </Box>
        </FlexBox>
        {entries.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setIsEditing(true)}
            sx={{
              textTransform: "none",
              borderColor: "#CBD5E1",
              color: "#334155",
            }}
          >
            Edit
          </Button>
        )}
      </FlexBox>

      {!entries.length ? (
        <Box
          py={6}
          textAlign="center"
          bgcolor="#F8FAFC"
          border="1px dashed"
          borderColor="#CBD5E1"
          borderRadius={2}
        >
          <Typography color="#64748B" fontSize={14} mb={2}>
            Qualify questions are not available
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setIsEditing(true)}
            sx={{ textTransform: "none" }}
          >
            Add questions
          </Button>
        </Box>
      ) : (
        <Stack gap={1.5}>
          {entries.map(([key, question], index) => (
            <QuestionCard
              key={`prequalify-${key}-${index}`}
              index={index}
              questionKey={key}
              question={question}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
